/**
 * Helper text to show when the text field is invalid
 */
import { string } from 'prop-types';
import {NormalParams} from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/NormalParams";

enum INPUT_RULE_DESCRIPTIONS {
  MEAN = 'mean must be a number',
  ERROR_FACTOR = 'Error Factor must be a >= 1',
  MEDIAN = 'median must be a number',
  STD = 'std must be a positive number',
  SCALE = 'Scale must be a non-negative number',
  SHAPE = 'Shape must be a non-negative number',
  TEST_INTERVAL = 'Time interval must be a non-negative number',
  PROBABILITY = 'Probability must be a number between 0 and 1',
  FAILURE_RATE = 'Failure rate must be a positive number',
  EMPTY = 'This field cannot be empty',
  MIN = 'Min cannot be be greater than max',
  NAME = 'Please enter a configuration name',
  QUANTIFICATION = 'Please enter a quantification type',
  MISSION_TIME = 'Mission time cannot be empty or 0',
  SAMPLING = 'Please select a sampling method',
  SAMPLE_NUMBER = 'Sample number cannot be a float or 0',
  CONFIDENCE_LEVEL = 'Confidence level must be between 0.5 and 1',
  K = 'k must be a non negative integer and less than or equal to the number of outgoing edges',
  PART_ID = 'Part must exist in the database',
  NORMAL_PARAMS = 'parameter type cannot be empty',
  P5 = 'p5 must be between 0 and 1, and less than p95',
  P95 = 'p95 must be between 0 and 1, and greater than p5',
  NODE_ID = 'No special characters: "$" or "."',
  
}

class InputRules {
  static DESCRIPTIONS = INPUT_RULE_DESCRIPTIONS;

  /**
   * @param {string} str
   */
  static isEmpty(str: string): boolean {
    return str === null || str === undefined || str.trim() === '';
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isNumber(str: string): boolean {
    return str !== '' && !isNaN(Number(str));
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isNotNegative(str: string): boolean {
    return InputRules.isNumber(str) && Number(str) >= 0;
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isPositive(str: string): boolean {
    return InputRules.isNumber(str) && Number(str) !== 0 && InputRules.isNotNegative(str);
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isProbability(str: string): boolean {
    return InputRules.isNumber(str) && Number(str) >= 0 && Number(str) <= 1;
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isScale(str: string): boolean {
    return InputRules.isNotNegative(str);
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isShape(str: string): boolean {
    return InputRules.isNotNegative(str);
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isMax(str: string): boolean {
    return InputRules.isNumber(str);
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isMin(str: string): boolean {
    return InputRules.isNumber(str);
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isTestInterval(str: string): boolean {
    return InputRules.isNotNegative(str);
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isStd(str: string): boolean {
    return InputRules.isPositive(str);
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isMean(str: string): boolean {
    return InputRules.isNumber(str);
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isMedian(str: string): boolean {
    return InputRules.isNumber(str);
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  static isFailureRate(str: string): boolean {
    return InputRules.isPositive(str);
  }

  /**
   * @param {number | string} mean
   * @return {boolean}
   */
  static isMeanValid(mean: number | string): boolean {
    return mean !== null && mean !== undefined && InputRules.isMean(mean.toString());
  }

  /**
   * @param {number | string} value
   * @return {boolean}
   */
  static isMedianValid(value: number | string): boolean {
    return value !== null && value !== undefined && InputRules.isMedian(value.toString());
  }

  /**
   * @param {number | string} value
   * @return {boolean}
   */
  static isGreaterThanOne(value: number | string): boolean {
    return value !== null && value !== undefined && value >= 1;
  }

  /**
   * @param {number | string} value
   * @return {boolean}
   */
  static isErrorFactorValid(value: number | string): boolean {
    return value !== null && value !== undefined && InputRules.isGreaterThanOne(value.toString());
  }


  /**
   * @param {number | string} std
   * @return {boolean}
   */
  static isStdValid(std: number | string): boolean {
    return std !== null && std !== undefined && InputRules.isStd(std.toString());
  }

  /**
   * @param {number | string} failureRate
   * @return {boolean}
   */
  static isFailureRateValid(failureRate: number | string): boolean {
    return failureRate !== null && failureRate !== undefined && InputRules.isFailureRate(failureRate.toString());
  }

  /**
   * @param {number | string} testInterval
   * @return {boolean}
   */
  static isTestIntervalValid(testInterval: number | string): boolean {
    return testInterval !== null && testInterval !== undefined && InputRules.isTestInterval(testInterval.toString());
  }

  /**
   * @param {number | string} shape
   * @return {boolean}
   */
  static isShapeValid(shape: number | string): boolean {
    return shape !== null && shape !== undefined && InputRules.isShape(shape.toString());
  }

  /**
   * @param {number | string} scale
   * @return {boolean}
   */
  static isScaleValid(scale: number | string): boolean {
    return scale !== null && scale !== undefined && InputRules.isScale(scale.toString());
  }

  /**
   * @param {number | string} probability
   * @return {boolean}
   */
  static isProbabilityValid(probability: number | string): boolean {
    return probability !== null && probability !== undefined && InputRules.isProbability(probability.toString());
  }

  /**
   * @param {number | string} max
   * @return {boolean}
   */
  static isMaxValid(max: number | string): boolean {
    return max !== null && max !== undefined && InputRules.isMax(max.toString());
  }

  /**
   * @param {number | string} min
   * @return {boolean}
   */
  static isMinValid(min: number | string): boolean {
    return min !== null && min !== undefined && InputRules.isMin(min.toString());
  }

  /**
   * @param {number | string} min
   * @param {number | string} max
   */
  static isMinGreaterThenMax(min: number | string, max: number | string): boolean {
    return min >= max;
  }

  /**
   * @param {string} sampleNumber
   */
  static isInteger(sampleNumber: string) {
    return InputRules.isNumber(sampleNumber) && Number.isInteger(Number(sampleNumber));
  }

  /**
   * @param {number[]} arr
   */
  static isEqualToOne(arr: number[]): boolean {
    return arr.reduce((acc, val) => { return acc + val; }, 0) === 1.0;
  }

  /**
   * @param {string[]} arr
   */
  static checkIfArrayIsUnique(arr: string[]): boolean {
    return arr.length === new Set(arr).size;
  }

  /**
   * @param {object} obj
   */
  static isObjectEmpty(obj: object): boolean {
    return Object.keys(obj).length === 0;
  }

  /**
   * @param {string} str
   */
  static isPositiveInteger(str: string) {
    return InputRules.isInteger(str) && InputRules.isPositive(str);
  }

  /**
   * @param {string} str
   */
  static isNonNegativeInteger(str: string) {
    return InputRules.isInteger(str) && InputRules.isNotNegative(str);
  }

  /**
   * @param {string} str
   */
  static isPositiveBetweenValues(str: string, min = 0.5, max = 1) {
    return InputRules.isPositive(str) && !InputRules.isEmpty(str) && Number(str) >= min && Number(str) <= max;
  }

  /**
   * @param {string} str
   */
  static isPositiveIntegerBetweenValues(str: string, min = 1, max = 1000) {
    return InputRules.isInteger(str) && InputRules.isPositiveBetweenValues(str, min, max);
  }

  static isNormalParamsValid(params: NormalParams) {
    return params !== undefined && params !== null;
  }

  static isP5Valid(p5: string, p95: string) {
    return InputRules.isProbability(p5) && InputRules.isProbability(p95) && Number(p5) < Number(p95);
  }

  static isP95Valid(p5: string, p95: string) {
    return InputRules.isProbability(p5) && InputRules.isProbability(p95) && Number(p5) < Number(p95);
  }

  static isNodeIdValid(id: string){
    return !/[$.]/g.test(id);
  }

}

export default InputRules;
